import React, { useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Divider,
  NumberFormatter,
  Tooltip
} from '@mantine/core';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Edit,
  InfoCircle
} from 'tabler-icons-react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import roundHalfEven from 'round-half-even';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import EditProductCard from '../products/EditProductCard';
import { ECOM_PRODUCT_PRICING_TYPE_ENUM } from '../../../config/constants';

const defaultValues = {
  maxVendorPrice: 0,
  minVendorPrice: 0,
  maxServiceFees: 0,
  minServiceFees: 0,
  maxBasePrice: 0,
  minBasePrice: 0,
  maxStorePrice: 0,
  minStorePrice: 0,
  maxRounding: 0,
  minRounding: 0,
  maxProfit: 0,
  minProfit: 0,
  minPricing: { amount: 0, type: '' },
  maxPricing: { amount: 0, type: '' }
};

const StoreProductPricingInfoCard = ({ ecomStoreProduct, onAction }) => {
  const [expandTax, setExpandTax] = useState(false);
  const {
    minVendorPrice,
    maxVendorPrice,
    minBasePrice,
    maxBasePrice,
    minStorePrice,
    maxStorePrice,
    maxServiceFees,
    minServiceFees,
    minRounding,
    maxRounding,
    minProfit,
    maxProfit,
    minPricing,
    maxPricing
  } = ecomStoreProduct?.ecomStoreProductVariants.reduce(
    (acc, variant) => {
      const {
        serviceFees,
        basePrice,
        vendorCost,
        price: storePrice,
        roundingAdjustment,
        pricingAmount,
        fkEcomPricingType
      } = variant;

      acc.maxVendorPrice = Math.max(acc.maxVendorPrice, vendorCost);
      acc.minVendorPrice =
        acc.minVendorPrice === 0
          ? vendorCost
          : Math.min(acc.minVendorPrice, vendorCost);

      acc.maxBasePrice = Math.max(acc.maxBasePrice, basePrice);
      acc.minBasePrice =
        acc.minBasePrice === 0
          ? basePrice
          : Math.min(acc.minBasePrice, basePrice);

      acc.maxStorePrice = Math.max(acc.maxStorePrice, storePrice);
      acc.minStorePrice =
        acc.minStorePrice === 0
          ? storePrice
          : Math.min(acc.minStorePrice, storePrice);

      acc.maxServiceFees = Math.max(acc.maxServiceFees, serviceFees);
      acc.minServiceFees =
        acc.minServiceFees === 0
          ? serviceFees
          : Math.min(acc.minServiceFees, serviceFees);

      acc.maxRounding = Math.max(acc.maxRounding, roundingAdjustment);
      acc.minRounding =
        acc.minRounding === 0
          ? roundingAdjustment
          : Math.min(acc.minRounding, roundingAdjustment);

      const profit = storePrice - vendorCost - serviceFees;
      acc.maxProfit = Math.max(acc.maxProfit, profit);
      acc.minProfit =
        acc.minProfit === 0 ? profit : Math.min(acc.minProfit, profit);

      const maxPricingAmount = Math.max(
        acc.maxPricing?.amount || 0,
        pricingAmount
      );
      acc.maxPricing = {
        amount: maxPricingAmount,
        type:
          maxPricingAmount === pricingAmount
            ? fkEcomPricingType
            : acc.maxPricing?.type || ''
      };

      const minPricingAmount =
        acc.minPricing?.amount === 0
          ? pricingAmount
          : Math.min(acc.minPricing?.amount || 0, pricingAmount);
      acc.minPricing = {
        amount: minPricingAmount,
        type:
          minPricingAmount === pricingAmount
            ? fkEcomPricingType
            : acc.minPricing?.type || ''
      };

      return acc;
    },
    { ...defaultValues }
  ) || { ...defaultValues };

  const taxInfo = (ecomStoreProduct.ecomStoreTax || []).reduce(
    (r, c) => {
      const tax = {
        ...c,
        minTaxAmount: roundHalfEven(minStorePrice * (c.taxRate / 100)),
        maxTaxAmount: roundHalfEven(maxStorePrice * (c.taxRate / 100))
      };

      return {
        totalTaxRate: r.totalTaxRate + c.taxRate,
        totalMinTax: r.totalMinTax + tax.minTaxAmount,
        totalMaxTax: r.totalMaxTax + tax.maxTaxAmount,
        taxes: [...r.taxes, tax]
      };
    },
    {
      totalTaxRate: 0,
      totalMinTax: 0,
      totalMaxTax: 0,
      taxes: []
    }
  );

  return (
    <EditProductCard
      contentProps={{ style: { padding: 0 } }}
      title="Pricing"
      titleAction={
        <Tooltip label="Edit pricing">
          <ActionIcon
            color="dark"
            onClick={onAction}
            size="lg"
            variant="subtle"
          >
            <Edit color="#000" size={20} />
          </ActionIcon>
        </Tooltip>
      }
    >
      <AppStack style={{ gap: 0 }}>
        <AppStack style={{ gap: 0, padding: '0px 16px 16px 16px' }}>
          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              Vendor price:
            </AppText>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={minVendorPrice / 100}
              />
              {minVendorPrice !== maxVendorPrice && (
                <>
                  {' '}
                  -{' '}
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={maxVendorPrice / 100}
                  />
                </>
              )}
            </AppText>
          </AppFlexbox>
          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <AppText style={{ fontSize: 14, color: '#666' }}>Pricing:</AppText>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix={
                  minPricing.type.toString() ===
                  ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT
                    ? '+$'
                    : '+'
                }
                style={{ fontSize: 14 }}
                suffix={
                  minPricing.type.toString() !==
                  ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT
                    ? '%'
                    : ''
                }
                thousandSeparator
                value={minPricing.amount / 100}
              />
              {minPricing.amount !== maxPricing.amount && (
                <>
                  {' '}
                  -{' '}
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix={
                      maxPricing.type.toString() ===
                      ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT
                        ? '+$'
                        : '+'
                    }
                    style={{ fontSize: 14 }}
                    suffix={
                      maxPricing.type.toString() !==
                      ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT
                        ? '%'
                        : ''
                    }
                    thousandSeparator
                    value={maxPricing.amount / 100}
                  />
                </>
              )}
            </AppText>
          </AppFlexbox>
          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <AppText style={{ fontSize: 14, color: '#666' }}>
              Base Price:
            </AppText>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={minBasePrice / 100}
              />
              {minBasePrice !== maxBasePrice && (
                <>
                  {' '}
                  -{' '}
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={maxBasePrice / 100}
                  />
                </>
              )}
            </AppText>
          </AppFlexbox>
          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <AppText style={{ fontSize: 14, color: '#666' }}>Fees:</AppText>

            <AppText style={{ fontSize: 14, color: '#666' }}>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={minServiceFees / 100}
              />
              {minServiceFees !== maxServiceFees && (
                <>
                  {' '}
                  -{' '}
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={maxServiceFees / 100}
                  />
                </>
              )}
            </AppText>
          </AppFlexbox>
          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <AppText style={{ fontSize: 14, color: '#666' }}>Rounding:</AppText>

            <AppText style={{ fontSize: 14, color: '#666' }}>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={minRounding / 100}
              />
              {minRounding !== maxRounding && (
                <>
                  {' '}
                  -{' '}
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={maxRounding / 100}
                  />
                </>
              )}
            </AppText>
          </AppFlexbox>

          <Divider style={{ margin: '4px 0px' }} />

          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              Store price:
            </AppText>

            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={minStorePrice / 100}
              />
              {minStorePrice !== maxStorePrice && (
                <>
                  {' '}
                  -{' '}
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={maxStorePrice / 100}
                  />
                </>
              )}
            </AppText>
          </AppFlexbox>

          {taxInfo.taxes.length > 0 && (
            <>
              <AppStack
                onClick={() => setExpandTax(!expandTax)}
                style={{ gap: 0, cursor: 'pointer' }}
              >
                <AppFlexbox
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    cursor: 'pointer'
                  }}
                >
                  <AppFlexbox style={{ alignItems: 'center', gap: 5 }}>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      Taxes {!expandTax && `(${taxInfo.totalTaxRate}%)`}
                    </AppText>
                    {expandTax ? (
                      <ChevronUp color="#666" size={16} />
                    ) : (
                      <ChevronDown color="#666" size={16} />
                    )}
                  </AppFlexbox>
                  {!expandTax && (
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      <NumberFormatter
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="$"
                        style={{ fontSize: 14 }}
                        thousandSeparator
                        value={taxInfo.totalMinTax / 100}
                      />
                      {taxInfo.totalMinTax !== taxInfo.totalMaxTax && (
                        <>
                          {' '}
                          -{' '}
                          <NumberFormatter
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            style={{ fontSize: 14 }}
                            thousandSeparator
                            value={taxInfo.totalMaxTax / 100}
                          />
                        </>
                      )}
                    </AppText>
                  )}
                </AppFlexbox>
                {expandTax && (
                  <AppStack style={{ gap: 0, paddingLeft: 20 }}>
                    {taxInfo.taxes.map((t) => (
                      <AppFlexbox
                        key={t.name}
                        style={{
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          cursor: 'pointer'
                        }}
                      >
                        <AppText style={{ fontSize: 14, color: '#666' }}>
                          {t.name} ({t.taxRate}%)
                        </AppText>
                        <AppText style={{ fontSize: 14, color: '#666' }}>
                          <NumberFormatter
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            style={{ fontSize: 14 }}
                            thousandSeparator
                            value={t.minTaxAmount / 100}
                          />
                          {t.minTaxAmount !== t.maxTaxAmount && (
                            <>
                              {' '}
                              -{' '}
                              <NumberFormatter
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$"
                                style={{ fontSize: 14 }}
                                thousandSeparator
                                value={t.maxTaxAmount / 100}
                              />
                            </>
                          )}
                        </AppText>
                      </AppFlexbox>
                    ))}
                  </AppStack>
                )}
              </AppStack>
              <AppFlexbox
                onChange={() => {}}
                style={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8
                }}
              >
                <AppText style={{ fontSize: 14, color: '#666' }}>
                  Final price
                </AppText>

                <AppText style={{ fontSize: 14, color: '#666' }}>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={(minStorePrice + taxInfo.totalMinTax) / 100}
                  />
                  {minStorePrice !== maxStorePrice && (
                    <>
                      {' '}
                      -{' '}
                      <NumberFormatter
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="$"
                        style={{ fontSize: 14 }}
                        thousandSeparator
                        value={(maxStorePrice + taxInfo.totalMaxTax) / 100}
                      />
                    </>
                  )}
                </AppText>
              </AppFlexbox>
            </>
          )}

          <Divider style={{ margin: '4px 0px' }} />

          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <AppText style={{ fontSize: 14, color: '#666' }}>
              Cost of goods:
            </AppText>

            <AppText style={{ fontSize: 14, color: '#666' }}>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={
                  (minVendorPrice + minServiceFees + taxInfo.totalMinTax) / 100
                }
              />
              {minProfit !== maxProfit && (
                <>
                  {' '}
                  -{' '}
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={
                      (maxVendorPrice + maxServiceFees + taxInfo.totalMaxTax) /
                      100
                    }
                  />
                </>
              )}
            </AppText>
          </AppFlexbox>

          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            <AppText style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>
              Profit:
            </AppText>

            <AppText style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={minProfit / 100}
              />
              {minStorePrice !== maxStorePrice && (
                <>
                  {' '}
                  -{' '}
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={maxProfit / 100}
                  />
                </>
              )}
            </AppText>
          </AppFlexbox>
        </AppStack>

        <Divider />
        <AppFlexbox
          style={{ padding: '8px 16px', gap: 8, alignItems: 'centere' }}
        >
          {ecomStoreProduct.isTaxExempt ? (
            <>
              <AlertCircle color="#eca70a" size={20} />
              <AppText
                style={{ fontSize: 14, fontWeight: 500, color: '#c40000' }}
              >
                This product is tax exempt
              </AppText>
            </>
          ) : (
            <>
              <InfoCircle color="dodgerblue" size={20} />
              <Anchor
                component={Link}
                style={{ fontSize: 14, color: 'dodgeblue' }}
                to="/merchant/settings/taxes"
              >
                Review your tax settings
              </Anchor>
            </>
          )}
        </AppFlexbox>
      </AppStack>
    </EditProductCard>
  );
};

StoreProductPricingInfoCard.propTypes = {
  ecomStoreProduct: PropTypes.object.isRequired,
  onAction: PropTypes.func.isRequired
};

export default StoreProductPricingInfoCard;
